import React, { useState, useEffect } from 'react';
import './DoctorAdmin.css';

const DoctorAdmin = () => {
    const [schedules, setSchedules] = useState([]);
    const [exceptions, setExceptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('schedule');
    const [editingSchedule, setEditingSchedule] = useState(null);
    const [newException, setNewException] = useState({
        exceptionDate: '',
        exceptionType: 'vacation',
        reason: '',
        isAvailable: false,
        startTime: '',
        endTime: ''
    });

    const dayNames = ['Duminică', 'Luni', 'Marți', 'Miercuri', 'Joi', 'Vineri', 'Sâmbătă'];
    const exceptionTypes = [
        { value: 'vacation', label: 'Concediu' },
        { value: 'holiday', label: 'Sărbătoare' },
        { value: 'sick_leave', label: 'Concediu Medical' },
        { value: 'conference', label: 'Conferință' },
        { value: 'other', label: 'Altele' }
    ];

    useEffect(() => {
        loadSchedules();
        loadExceptions();
    }, []);

    const loadSchedules = async () => {
        try {
            // For testing purposes, use a mock doctor ID
            const response = await fetch('/api/doctor-schedules?doctorId=1', {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setSchedules(data.schedules || []);
            } else {
                setError('Nu s-au putut încărca programele');
            }
        } catch (err) {
            setError('Eroare la încărcarea programelor: ' + err.message);
        }
    };

    const loadExceptions = async () => {
        try {
            const response = await fetch('/api/doctor-schedules/exceptions?doctorId=1', {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setExceptions(data.exceptions || []);
            } else {
                setError('Nu s-au putut încărca excepțiile');
            }
        } catch (err) {
            setError('Eroare la încărcarea excepțiilor: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const saveSchedule = async (scheduleData) => {
        try {
            // Add doctorId for testing
            const dataWithDoctorId = { ...scheduleData, doctorId: 1 };
            
            const response = await fetch('/api/doctor-schedules', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(dataWithDoctorId)
            });

            if (response.ok) {
                await loadSchedules();
                setEditingSchedule(null);
                setError('');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Nu s-a putut salva programul');
            }
        } catch (err) {
            setError('Eroare la salvarea programului: ' + err.message);
        }
    };

    const deleteSchedule = async (scheduleId) => {
        if (!window.confirm('Are you sure you want to delete this schedule?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/doctor-schedules/${scheduleId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await loadSchedules();
                setError('');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to delete schedule');
            }
        } catch (err) {
            setError('Error deleting schedule: ' + err.message);
        }
    };

    const saveException = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch('/api/doctor-schedules/exceptions', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newException)
            });

            if (response.ok) {
                await loadExceptions();
                setNewException({
                    exceptionDate: '',
                    exceptionType: 'vacation',
                    reason: '',
                    isAvailable: false,
                    startTime: '',
                    endTime: ''
                });
                setError('');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to save exception');
            }
        } catch (err) {
            setError('Error saving exception: ' + err.message);
        }
    };

    const deleteException = async (exceptionId) => {
        if (!window.confirm('Are you sure you want to delete this exception?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`/api/doctor-schedules/exceptions/${exceptionId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                await loadExceptions();
                setError('');
            } else {
                const errorData = await response.json();
                setError(errorData.error || 'Failed to delete exception');
            }
        } catch (err) {
            setError('Error deleting exception: ' + err.message);
        }
    };

    const formatTime = (timeString) => {
        if (!timeString) return '';
        const date = new Date(`1970-01-01T${timeString}`);
        return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: false 
        });
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return <div className="doctor-admin-loading">Se încarcă panoul de administrare...</div>;
    }

    return (
        <div className="doctor-admin">
            <div className="doctor-admin-header">
                <h1>Panou de Administrare Doctor</h1>
                <p>Gestionează programul și disponibilitatea</p>
            </div>

            {error && (
                <div className="doctor-admin-error">
                    {error}
                </div>
            )}

            <div className="doctor-admin-tabs">
                <button 
                    className={`tab-button ${activeTab === 'schedule' ? 'active' : ''}`}
                    onClick={() => setActiveTab('schedule')}
                >
                    Program Săptămânal
                </button>
                <button 
                    className={`tab-button ${activeTab === 'exceptions' ? 'active' : ''}`}
                    onClick={() => setActiveTab('exceptions')}
                >
                    Excepții Program
                </button>
            </div>

            {activeTab === 'schedule' && (
                <div className="schedule-tab">
                    <div className="schedule-header">
                        <h2>Program Săptămânal</h2>
                        <button 
                            className="add-schedule-btn"
                            onClick={() => setEditingSchedule({ 
                                dayOfWeek: 1, 
                                startTime: '09:00', 
                                endTime: '17:00', 
                                slotDurationMinutes: 20,
                                isActive: true 
                            })}
                        >
                            Adaugă Program
                        </button>
                    </div>

                    <div className="schedule-list">
                        {dayNames.map((dayName, dayIndex) => {
                            const daySchedule = schedules.find(s => s.day_of_week === dayIndex);
                            return (
                                <div key={dayIndex} className="schedule-day">
                                    <div className="day-name">{dayName}</div>
                                    {daySchedule ? (
                                        <div className="day-schedule">
                                            <div className="schedule-info">
                                                <span className="time-range">
                                                    {formatTime(daySchedule.start_time)} - {formatTime(daySchedule.end_time)}
                                                </span>
                                                <span className="slot-duration">
                                                    {daySchedule.slot_duration_minutes} min slots
                                                </span>
                                                <span className={`status ${daySchedule.is_active ? 'active' : 'inactive'}`}>
                                                    {daySchedule.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div className="schedule-actions">
                                                <button 
                                                    className="edit-btn"
                                                    onClick={() => setEditingSchedule({
                                                        ...daySchedule,
                                                        startTime: formatTime(daySchedule.start_time),
                                                        endTime: formatTime(daySchedule.end_time)
                                                    })}
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    className="delete-btn"
                                                    onClick={() => deleteSchedule(daySchedule.schedule_id)}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="no-schedule">
                                            <span>Niciun program setat</span>
                                            <button 
                                                className="add-btn"
                                                onClick={() => setEditingSchedule({ 
                                                    dayOfWeek: dayIndex, 
                                                    startTime: '09:00', 
                                                    endTime: '17:00', 
                                                    slotDurationMinutes: 20,
                                                    isActive: true 
                                                })}
                                            >
                                                Adaugă
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {editingSchedule && (
                        <div className="schedule-modal">
                            <div className="modal-content">
                                <h3>
                                    {editingSchedule.schedule_id ? 'Edit' : 'Add'} Schedule - {dayNames[editingSchedule.dayOfWeek || editingSchedule.day_of_week]}
                                </h3>
                                <div className="form-group">
                                    <label>Start Time:</label>
                                    <input
                                        type="time"
                                        value={editingSchedule.startTime || formatTime(editingSchedule.start_time)}
                                        onChange={(e) => setEditingSchedule({
                                            ...editingSchedule,
                                            startTime: e.target.value
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>End Time:</label>
                                    <input
                                        type="time"
                                        value={editingSchedule.endTime || formatTime(editingSchedule.end_time)}
                                        onChange={(e) => setEditingSchedule({
                                            ...editingSchedule,
                                            endTime: e.target.value
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slot Duration (minutes):</label>
                                    <select
                                        value={editingSchedule.slotDurationMinutes || editingSchedule.slot_duration_minutes}
                                        onChange={(e) => setEditingSchedule({
                                            ...editingSchedule,
                                            slotDurationMinutes: parseInt(e.target.value)
                                        })}
                                    >
                                        <option value={15}>15 minutes</option>
                                        <option value={20}>20 minutes</option>
                                        <option value={30}>30 minutes</option>
                                        <option value={45}>45 minutes</option>
                                        <option value={60}>60 minutes</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={editingSchedule.isActive !== undefined ? editingSchedule.isActive : editingSchedule.is_active}
                                            onChange={(e) => setEditingSchedule({
                                                ...editingSchedule,
                                                isActive: e.target.checked
                                            })}
                                        />
                                        Active
                                    </label>
                                </div>
                                <div className="modal-actions">
                                    <button 
                                        className="save-btn"
                                        onClick={() => saveSchedule({
                                            dayOfWeek: editingSchedule.dayOfWeek || editingSchedule.day_of_week,
                                            startTime: editingSchedule.startTime || formatTime(editingSchedule.start_time),
                                            endTime: editingSchedule.endTime || formatTime(editingSchedule.end_time),
                                            slotDurationMinutes: editingSchedule.slotDurationMinutes || editingSchedule.slot_duration_minutes,
                                            isActive: editingSchedule.isActive !== undefined ? editingSchedule.isActive : editingSchedule.is_active
                                        })}
                                    >
                                        Save
                                    </button>
                                    <button 
                                        className="cancel-btn"
                                        onClick={() => setEditingSchedule(null)}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {activeTab === 'exceptions' && (
                <div className="exceptions-tab">
                    <div className="exceptions-header">
                        <h2>Schedule Exceptions</h2>
                    </div>

                    <div className="add-exception">
                        <h3>Add New Exception</h3>
                        <div className="exception-form">
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Date:</label>
                                    <input
                                        type="date"
                                        value={newException.exceptionDate}
                                        onChange={(e) => setNewException({
                                            ...newException,
                                            exceptionDate: e.target.value
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Type:</label>
                                    <select
                                        value={newException.exceptionType}
                                        onChange={(e) => setNewException({
                                            ...newException,
                                            exceptionType: e.target.value
                                        })}
                                    >
                                        {exceptionTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Reason:</label>
                                    <input
                                        type="text"
                                        value={newException.reason}
                                        onChange={(e) => setNewException({
                                            ...newException,
                                            reason: e.target.value
                                        })}
                                        placeholder="Optional reason"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={newException.isAvailable}
                                            onChange={(e) => setNewException({
                                                ...newException,
                                                isAvailable: e.target.checked
                                            })}
                                        />
                                        Still Available (partial day)
                                    </label>
                                </div>
                            </div>
                            {newException.isAvailable && (
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Available From:</label>
                                        <input
                                            type="time"
                                            value={newException.startTime}
                                            onChange={(e) => setNewException({
                                                ...newException,
                                                startTime: e.target.value
                                            })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Available Until:</label>
                                        <input
                                            type="time"
                                            value={newException.endTime}
                                            onChange={(e) => setNewException({
                                                ...newException,
                                                endTime: e.target.value
                                            })}
                                        />
                                    </div>
                                </div>
                            )}
                            <button 
                                className="add-exception-btn"
                                onClick={saveException}
                                disabled={!newException.exceptionDate}
                            >
                                Add Exception
                            </button>
                        </div>
                    </div>

                    <div className="exceptions-list">
                        <h3>Current Exceptions</h3>
                        {exceptions.length === 0 ? (
                            <p>No schedule exceptions found.</p>
                        ) : (
                            <div className="exceptions-grid">
                                {exceptions.map(exception => (
                                    <div key={exception.exception_id} className="exception-card">
                                        <div className="exception-header">
                                            <span className="exception-date">
                                                {formatDate(exception.exception_date)}
                                            </span>
                                            <span className={`exception-type ${exception.exception_type}`}>
                                                {exceptionTypes.find(t => t.value === exception.exception_type)?.label}
                                            </span>
                                        </div>
                                        {exception.reason && (
                                            <div className="exception-reason">
                                                {exception.reason}
                                            </div>
                                        )}
                                        {exception.is_available && (
                                            <div className="exception-availability">
                                                Available: {formatTime(exception.start_time)} - {formatTime(exception.end_time)}
                                            </div>
                                        )}
                                        <button 
                                            className="delete-exception-btn"
                                            onClick={() => deleteException(exception.exception_id)}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default DoctorAdmin;
